// Workspace roles in the UI: labels, descriptions and hooks. The rules themselves live in
// shared/src/roles.ts and are enforced by the server (server/src/access.ts).
import type { SharePermission } from '@shared/types';
import { canManage, isGuest, roleOf, type Role } from '@shared/roles';
import { Eye, MessageCircle, Pencil, SquarePen, User, UserCrown, UserShield, UserTag, type IconComponent } from '@/components/ui/icons';
import { useDb, useMe } from './store';
import { useWorkspaceId } from './nav';

export const ROLES: Record<Role, { label: string; plural: string; description: string; icon: IconComponent }> = {
  owner: { label: 'Owner', plural: 'Owner', description: 'Created the workspace or had it handed over. Only one per workspace.', icon: UserCrown },
  admin: { label: 'Admin', plural: 'Admins', description: 'Can manage Spaces, People and other workspace settings.', icon: UserShield },
  member: { label: 'Member', plural: 'Members', description: 'Can access all public items in your workspace.', icon: User },
  guest: { label: 'Guest', plural: 'Guests', description: 'Can’t use all features or be added to Spaces. Can only access items shared with them.', icon: UserTag },
};

/** The roles someone can be given from the People page, in menu order. */
export const ASSIGNABLE_ROLES: Exclude<Role, 'owner'>[] = ['member', 'guest', 'admin'];

export const SHARE_LEVELS: Record<SharePermission, { label: string; description: string; icon: IconComponent }> = {
  full: { label: 'Full edit', description: 'Can edit, add and delete', icon: SquarePen },
  edit: { label: 'Edit', description: 'Can edit, but not delete', icon: Pencil },
  comment: { label: 'Comment', description: 'Can comment, but not edit', icon: MessageCircle },
  view: { label: 'View only', description: 'Can view, but not comment or edit', icon: Eye },
};

/** Someone's role in a workspace (the current one by default). */
export function useRole(userId: string | null | undefined, workspaceId?: string): Role {
  const current = useWorkspaceId();
  const ws = workspaceId ?? current;
  return useDb((d) => roleOf(d.workspaces[ws], userId));
}

export function useMyRole(workspaceId?: string): Role {
  return useRole(useMe(), workspaceId);
}

export const useIsGuest = (workspaceId?: string) => isGuest(useMyRole(workspaceId));
export const useCanManage = (workspaceId?: string) => canManage(useMyRole(workspaceId));
